import { Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  BookOpenText, BriefcaseBusiness, CheckCheck,
  ChevronDown, ChevronLeft, FileSearch, HandCoins, History, Landmark, LayoutDashboard,
  LogOut, Menu, MessageCircle, Moon, PiggyBank, Settings2, ShieldCheck,
  Sun, UsersRound, X,
} from 'lucide-react'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { useTheme } from './ThemeProvider'
import { BankMark } from './BankMark'

const nav = [
  { to: '/', label: 'Home', icon: LayoutDashboard, primary: true, permissions: ['dashboard.read'] },
  { to: '/members', label: 'Members', icon: UsersRound, primary: true, permissions: ['member.read', 'member.manage'] },
  { to: '/loans', label: 'Loans', icon: HandCoins, primary: true, permissions: ['loan.read', 'loan.manage', 'loan.approve'] },
  { to: '/pigmy', label: 'Pigmy', icon: PiggyBank, primary: true, permissions: ['pigmy.collection.read', 'pigmy.collection.record', 'pigmy.collection.handover', 'pigmy.collection.approve'] },
  { to: '/approvals', label: 'Approvals', icon: CheckCheck, primary: false, permissions: ['approval.read', 'journal.approve', 'loan.approve', 'evidence.review'] },
  { to: '/accounting', label: 'Accounting', icon: BookOpenText, primary: false, permissions: ['journal.read', 'journal.create', 'report.trial_balance'] },
  { to: '/operations', label: 'Back office', icon: BriefcaseBusiness, primary: false, permissions: ['product.read', 'deposit.read', 'share.read', 'agent.read', 'standing_instruction.read', 'deposit.interest.run', 'bank.reconciliation.read', 'business_day.review', 'notification.read', 'dividend.read', 'investment.read', 'provision.read', 'document.manage', 'organization.read'] },
  { to: '/reports', label: 'Reports', icon: FileSearch, primary: false, permissions: ['report.catalog.read'] },
  { to: '/audit-trail', label: 'Audit trail', icon: History, primary: false, permissions: ['audit.read'] },
  { to: '/messages', label: 'Messages', icon: MessageCircle, primary: false, permissions: ['admin.messaging'] },
  { to: '/admin', label: 'Administration', icon: Settings2, primary: false, permissions: ['admin.users', 'migration.restore.read', 'document.checklist.manage'] },
] as const

type NavItem = (typeof nav)[number]

export function ProtectedLayout() {
  const { user, loading, canAny, logout } = useAuth()
  const { theme, toggle } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const items = useMemo(() => nav.filter((item) => canAny(...item.permissions)), [canAny])

  if (loading) return <div className="app-loading"><BankMark size={50} /><span>Opening your workbench…</span></div>
  if (!user) return <Navigate to="/login" />

  const initials = user.display_name.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase()

  return <div className="app-shell">
    <a href="#main-content" className="skip-link">Skip to content</a>
    <header className="topbar">
      <Link to="/" className="brand" aria-label="Sahakari home">
        <BankMark size={38} /><span><strong>Sahakari</strong><small>Cooperative banking</small></span>
      </Link>
      <AdaptiveNavigation items={items} />
      <div className="branch-date" title={user.home_branch_name} aria-label={`Home branch ${user.home_branch_code}, ${user.home_branch_name}`}><span>{user.home_branch_code}</span><strong>{new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date())}</strong></div>
      <button className="icon-button" onClick={toggle} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
        {theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
      </button>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          <button className="user-chip" aria-label={`Open user menu for ${user.display_name}`}>
            <span>{user.display_name}</span>
            <b aria-hidden>{initials || 'U'}</b>
            <ChevronDown className="user-chip-chevron" size={14} aria-hidden />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="user-menu" side="bottom" align="end" sideOffset={10} collisionPadding={12}>
            <div className="user-menu-identity">
              <small>Signed in as</small>
              <strong>{user.username}</strong>
            </div>
            <DropdownMenu.Separator className="user-menu-separator" />
            <DropdownMenu.Item className="user-menu-signout" onSelect={() => void logout()}>
              <LogOut size={17} aria-hidden />
              <span>Sign out</span>
            </DropdownMenu.Item>
            <DropdownMenu.Arrow className="user-menu-arrow" width={13} height={7} />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <button className="mobile-menu-button" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu /></button>
    </header>
    <div className="day-ribbon"><ShieldCheck size={14} /><span>Secure banking session</span><i />Actions are limited to your assigned duties</div>
    <aside className={`sidebar ${menuOpen ? 'sidebar-open' : ''}`} aria-label="Primary navigation">
      <div className="sidebar-mobile-head"><span>Workspaces</span><button onClick={() => setMenuOpen(false)} aria-label="Close navigation"><X /></button></div>
      <nav>{items.map(({ to, label, icon: Icon }) => <Link key={to} to={to} onClick={() => setMenuOpen(false)} activeOptions={{ exact: to === '/' }} className="nav-link" activeProps={{ 'data-active': true }}>
        <Icon size={18} aria-hidden /><span>{label}</span>
      </Link>)}</nav>
    </aside>
    {menuOpen && <button className="nav-scrim" onClick={() => setMenuOpen(false)} aria-label="Close navigation" />}
    <main id="main-content" className="app-main"><Outlet /></main>
  </div>
}

function AdaptiveNavigation({ items }: { items: readonly NavItem[] }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visiblePaths, setVisiblePaths] = useState<readonly string[]>(() => items.map((item) => item.to))
  const isActive = useCallback((item: NavItem) => item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`), [pathname])

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return
    const calculate = () => {
      const itemNodes = Array.from(measure.querySelectorAll<HTMLElement>('[data-nav-measure="item"]'))
      const moreNode = measure.querySelector<HTMLElement>('[data-nav-measure="more"]')
      if (itemNodes.length !== items.length || !moreNode) return
      const available = container.clientWidth
      const gap = 3
      const widths = itemNodes.map((node) => node.getBoundingClientRect().width)
      const fullWidth = widths.reduce((total, width) => total + width, 0) + Math.max(0, widths.length - 1) * gap
      if (fullWidth <= available) {
        setVisiblePaths(items.map((item) => item.to))
        return
      }
      const moreWidth = moreNode.getBoundingClientRect().width
      let used = moreWidth
      let count = 0
      for (const width of widths) {
        const next = used + width + gap
        if (next > available) break
        used = next
        count += 1
      }
      count = Math.max(1, Math.min(count, items.length - 1))
      const visible = new Set(items.slice(0, count).map((item) => item.to))
      const active = items.find(isActive)
      if (active && !visible.has(active.to)) {
        const replace = [...visible].at(-1)
        if (replace) visible.delete(replace)
        visible.add(active.to)
      }
      setVisiblePaths(items.filter((item) => visible.has(item.to)).map((item) => item.to))
    }
    const observer = new ResizeObserver(calculate)
    observer.observe(container)
    calculate()
    void document.fonts?.ready.then(calculate)
    return () => observer.disconnect()
  }, [isActive, items])

  const visible = items.filter((item) => visiblePaths.includes(item.to))
  const overflow = items.filter((item) => !visiblePaths.includes(item.to))
  return <div className="desktop-nav-shell" ref={containerRef}>
    <nav className="desktop-nav" aria-label="Main navigation">
      {visible.map(({ to, label }) => <Link key={to} to={to} activeOptions={{ exact: to === '/' }} activeProps={{ 'data-active': true }}>{label}</Link>)}
      {overflow.length > 0 && <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger className="desktop-nav-more" data-active={overflow.some(isActive) || undefined}>More <ChevronDown size={13} aria-hidden /></DropdownMenu.Trigger>
        <DropdownMenu.Portal><DropdownMenu.Content className="workspace-menu" side="bottom" align="start" sideOffset={9} collisionPadding={12}>
          {overflow.map(({ to, label, icon: Icon }) => <DropdownMenu.Item asChild key={to}><Link to={to} activeOptions={{ exact: to === '/' }} activeProps={{ 'data-active': true }}><Icon size={16} aria-hidden />{label}</Link></DropdownMenu.Item>)}
        </DropdownMenu.Content></DropdownMenu.Portal>
      </DropdownMenu.Root>}
    </nav>
    <div ref={measureRef} className="desktop-nav-measure" aria-hidden>
      {items.map((item) => <span key={item.to} data-nav-measure="item">{item.label}</span>)}
      <span data-nav-measure="more">More <ChevronDown size={13} /></span>
    </div>
  </div>
}

export function PermissionDenied() {
  return <div className="permission-page"><Landmark size={42} /><h1>This desk is not assigned to you</h1><p>Your permissions do not allow this workspace. Ask an administrator if your duties have changed.</p><Link to="/" className="button button-primary"><ChevronLeft size={16} /> Return to overview</Link></div>
}
